import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../store/store';
import { searchAsync, selectSearchState, selectVendorResults } from '../store/slices/searchSlice';
import UnifiedSearchInterface from './UnifiedSearchInterface';
import DynamicResultsDisplay from './DynamicResultsDisplay';
import SolicitationFilter from './SolicitationFilter';

// Example queries per irregularity type
const EXAMPLE_QUERIES: Record<string, string[]> = {
  'competition-analysis': [
    'negotiated contracts with only one offer',
    'full and open competition with 0 bidders',
    'contracts marked competitive but single bidder',
    'sole source military equipment SSS',
    'fake competition detection analysis'
  ],
  'sole-source': [
    'IT services in Virginia 2024 sole source',
    'Construction contracts over $1M sole source',
    'DoD contracts in Wisconsin 2022 sole source',
    'Software licenses without competition'
  ],
  'low-competition': [
    'contracts with only 2 bidders',
    'IT services limited competition 2024',
    'construction awards with 3 or fewer offers',
    'limited bidder competitions in Maryland'
  ],
  'excessive-pricing': [
    'hammers over $100 each',
    'laptops above $3000 per unit',
    'office supplies excessive pricing',
    'tools priced above market rate'
  ],
  'split-purchase': [
    'same vendor multiple orders within 48 hours',
    'contracts just under $10K micro-purchase threshold',
    'orders just under $250K SAT threshold',
    'purchases near $750K R&D threshold'
  ],
  'vendor-concentration': [
    'Maryland vendors with multiple contracts NAICS 541511',
    'Texas construction vendor concentration',
    'vendors with >5 contracts in Florida',
    'single vendor dominance analysis'
  ],
  'repeat-awards': [
    'same vendors repeated awards',
    'vendor receiving multiple contracts quarterly',
    'repeat award patterns in construction',
    'recurring vendor awards analysis'
  ],
  'timing-anomaly': [
    'contracts awarded in September 2024',
    'construction contracts',
    'IT services awards',
    'office supplies purchases'
  ],
  'emergency-misuse': [
    'emergency furniture purchases',
    'COVID emergency office supplies 2023',
    'disaster relief IT equipment routine',
    'office supplies as emergency'
  ]
};

// Search placeholders per type
const SEARCH_PLACEHOLDERS: Record<string, string> = {
  'competition-analysis': 'e.g., negotiated contracts with one offer, fake competition',
  'sole-source': 'e.g., IT services sole source in Maryland',
  'low-competition': 'e.g., contracts with 2-3 bidders only',
  'excessive-pricing': 'e.g., laptops over $3000 each',
  'split-purchase': 'e.g., contracts near $10K, $250K, $750K, or $1M thresholds',
  'vendor-concentration': 'e.g., vendors with >5 contracts in Texas',
  'repeat-awards': 'e.g., same vendors repeated awards',
  'timing-anomaly': 'e.g., weekend awards in September',
  'emergency-misuse': 'e.g., emergency furniture purchases'
};

const InvestigatorPage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const searchState = useAppSelector(selectSearchState);
  const vendorResults = useAppSelector(selectVendorResults);
  const [selectedQuery, setSelectedQuery] = useState<string>('');
  const [timingType, setTimingType] = useState<string>('fiscal_year_end');
  const [solicitationCode, setSolicitationCode] = useState<string | string[]>('');

  // Redirect to competition-analysis if no type specified
  useEffect(() => {
    if (!type) {
      navigate('/investigator/competition-analysis', { replace: true });
    }
  }, [type, navigate]);

  // Validate irregularity type
  const isValidType = type && Object.keys(EXAMPLE_QUERIES).includes(type);

  if (!type || !isValidType) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Invalid irregularity type. Redirecting to sole source investigation...
        </Alert>
      </Box>
    );
  }

  const handleExampleQuery = (query: string) => {
    setSelectedQuery(query);
    // Trigger search with the example query
    const searchParams: any = {
      query,
      sources: ['all'],
      max_results: 50,
      include_explanation: true,
      irregularity_type: type
    };
    
    // Add timing type filter for timing-anomaly searches
    if (type === 'timing-anomaly') {
      searchParams.filters = { timing_type: timingType };
    }
    
    dispatch(searchAsync(searchParams));
  };

  const handleSearch = async (searchParams: any) => {
    // Add irregularity type to search parameters
    const enhancedParams = {
      ...searchParams,
      irregularity_type: type
    };
    
    // Add timing type filter for timing-anomaly searches
    if (type === 'timing-anomaly') {
      enhancedParams.filters = {
        ...enhancedParams.filters,
        timing_type: timingType
      };
    }
    
    // Add solicitation code filter for competition-analysis
    if (type === 'competition-analysis' && solicitationCode) {
      enhancedParams.filters = {
        ...enhancedParams.filters,
        solicitation_procedures_code: solicitationCode
      };
    }
    
    await dispatch(searchAsync(enhancedParams));
  };

  const exampleQueries = EXAMPLE_QUERIES[type] || [];
  const placeholder = SEARCH_PLACEHOLDERS[type] || 'Enter your search query...';

  return (
    <Box>
      {/* Solicitation Type Filter for competition-analysis - HIDDEN */}
      {/* {type === 'competition-analysis' && (
        <Card sx={{ mb: 3, boxShadow: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              🔍 Filter by Solicitation Type
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Filter results by specific solicitation procedure codes to focus your investigation
            </Typography>
            <SolicitationFilter
              value={solicitationCode}
              onChange={setSolicitationCode}
              multiple={false}
              showDescription={true}
              size="medium"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              • SSS: True sole source (10.20% of contracts)
              • NP: Negotiated contracts (54.71% - check for fake competition)
              • MAFO: Multiple award (19.97% - check for single bidder)
            </Typography>
          </CardContent>
        </Card>
      )} */}
      
      {/* Timing Type Selector for timing-anomaly */}
      {type === 'timing-anomaly' && (
        <Card sx={{ mb: 3, boxShadow: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              ⏰ Select Timing Pattern Type
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Timing Pattern</InputLabel>
              <Select
                value={timingType}
                onChange={(e) => setTimingType(e.target.value)}
                label="Timing Pattern"
              >
                <MenuItem value="fiscal_year_end">
                  <Box>
                    <Typography variant="body1">Fiscal Year-End Rush (Sept 15-30)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      CRITICAL RISK - "Use it or lose it" spending surge
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="quarter_end">
                  <Box>
                    <Typography variant="body1">Quarter-End Periods</Typography>
                    <Typography variant="caption" color="text.secondary">
                      HIGH RISK - Last 5 days of fiscal quarters
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="holiday_period">
                  <Box>
                    <Typography variant="body1">Holiday Periods</Typography>
                    <Typography variant="caption" color="text.secondary">
                      HIGH RISK - Thanksgiving, Christmas, July 4th, etc.
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="weekend_awards">
                  <Box>
                    <Typography variant="body1">Weekend Awards</Typography>
                    <Typography variant="caption" color="text.secondary">
                      HIGH RISK - Saturday/Sunday contract awards
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="month_end">
                  <Box>
                    <Typography variant="body1">Month-End Rush</Typography>
                    <Typography variant="caption" color="text.secondary">
                      MEDIUM RISK - Last 3 business days of month
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="after_hours">
                  <Box>
                    <Typography variant="body1">After-Hours Awards</Typography>
                    <Typography variant="caption" color="text.secondary">
                      MEDIUM RISK - Contracts outside business hours
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="emergency_period">
                  <Box>
                    <Typography variant="body1">Emergency Declaration Period</Typography>
                    <Typography variant="caption" color="text.secondary">
                      CRITICAL RISK - 30 days after emergency declaration
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
              <FormHelperText>
                Select a timing pattern to search for contracts awarded during suspicious time periods
              </FormHelperText>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              This will filter contracts to only show those awarded during the selected time period. 
              You can still add additional search terms to narrow down results further.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Search Interface */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent sx={{ pb: 2 }}>
          <UnifiedSearchInterface 
            onSearch={handleSearch}
            placeholder={placeholder}
            initialQuery={selectedQuery}
            currentIrregularityType={type}
            showTypeSelector={false}
            showHeader={false}
          />
        </CardContent>
      </Card>

      {/* Example Queries */}
      {exampleQueries.length > 0 && (
        <Card sx={{ mb: 3, boxShadow: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              💡 Example Investigations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Try these example queries to see how this irregularity type works:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {exampleQueries.map((query, index) => (
                <Chip
                  key={index}
                  label={query}
                  onClick={() => handleExampleQuery(query)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'white'
                    }
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {searchState.loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Analyzing {type.replace('-', ' ')} patterns...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {searchState.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {searchState.error}
        </Alert>
      )}

      {/* Results Display */}
      {((searchState.results && searchState.results.length > 0) || 
        (vendorResults && vendorResults.length > 0)) && (
        <DynamicResultsDisplay />
      )}

      {/* No Results State */}
      {!searchState.loading && 
       searchState.results && searchState.results.length === 0 && 
       vendorResults && vendorResults.length === 0 && 
       searchState.query && (
        <Card sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="h6" gutterBottom>
            No {type.replace('-', ' ')} irregularities found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your search didn't return any results matching the {type.replace('-', ' ')} detection criteria.
            Try adjusting your query or using one of the example searches above.
          </Typography>
          <Button 
            variant="outlined" 
            onClick={() => setSelectedQuery('')}
          >
            Try Different Query
          </Button>
        </Card>
      )}

      {/* Welcome State */}
      {!searchState.loading && !searchState.query && (
        <Card sx={{ textAlign: 'center', p: 4, backgroundColor: 'background.paper' }}>
          <Typography variant="h5" gutterBottom>
            🔍 Ready to investigate {type.replace('-', ' ')} irregularities
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
            Enter a search query above or click on one of the example investigations to get started. 
            This tool will analyze the data for {type.replace('-', ' ')} patterns and provide detailed findings.
          </Typography>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Button 
                variant="contained" 
                onClick={() => handleExampleQuery(exampleQueries[0])}
                disabled={exampleQueries.length === 0}
              >
                Try Example Search
              </Button>
            </Grid>
            <Grid item>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/')}
              >
                Back to Standard Search
              </Button>
            </Grid>
          </Grid>
        </Card>
      )}
    </Box>
  );
};

export default InvestigatorPage;